import React, { useState, useEffect } from 'react';
import {
  Button,
  Spinner,
  Alert,
  Input,
  Form,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { createSubTask } from '../../Api/ApiUrlConst';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../Context/AuthContext';
import CancelIcon from '@mui/icons-material/Cancel';

interface AddSubTaskInlineProps {
  taskId: number;
  projectId: string;
  teamMembers: TeamMemberDTO[];
  parentSubTaskId?: number | null;
  /** Added optional parentTaskId to match caller that passes parentTaskId={task.id} */
  parentTaskId?: number | null;
  parentTaskDueDate: string;
  onCancel: () => void;
  onSubTaskAdded: () => void;
}




export interface TeamMemberDTO {
  userID: string;
  name: string;
  department: string;
}

interface SubTaskDTO {
  taskId: number;
  parentSubTaskId: number | null;
  description: string;
  assignedToId: string;
  dueDate: string | null;
  status: string;
  priority: string;
  percentCompleted: number;
  workHours: number;
  completionDate: string | null;
  remarks: string;
  issues: string;
}

const statusOptions = [
  { value: "YetToStart", label: "Yet to Start" },
  { value: "WaitingForInput", label: "Waiting for Input" },
  { value: "", label: "" },
  { value: "ModellingTodo", label: "Modelling ToDo" },
  { value: "ModellingInProgress", label: "Modelling in Progress" },
  { value: "ModellingCompleted", label: "Modelling Completed" },
  { value: "", label: "" },
  { value: "DraftingTodo", label: "Drafting ToDo" },
  { value: "DraftingInProgress", label: "Drafting in Progress" },
  { value: "DraftingCompleted", label: "Drafting Completed" },
  { value: "", label: "" },
  { value: "QCTodo", label: "QC ToDo" },
  { value: "QCInProgress", label: "QC in Progress" },
  { value: "QCCompleted", label: "QC Completed" },
  { value: "", label: "" },
  { value: "ClientReviewTodo", label: "Client Review ToDo" },
  { value: "ClientReviewInProgress", label: "Client Review in Progress" },
  { value: "ClientReviewCompleted", label: "Client Review Completed" },
  { value: "", label: "" },
  { value: "FinalCorrectionTodo", label: "Final Correction ToDo" },
  { value: "FinalCorrectionInProgress", label: "Final Correction in Progress" },
  { value: "FinalCorrectionCompleted", label: "Final Correction Completed" },
  { value: "", label: "" },
  { value: "RevisionTodo", label: "Revision ToDo" },
  { value: "RevisionInProgress", label: "Revision in Progress" },
  { value: "RevisionCompleted", label: "Revision Completed" },
  { value: "", label: "" },
  { value: "Cancelled", label: "Cancelled" },
  { value: 'Issues', label: 'Issues' },
  { value: "OnHold", label: "On Hold" },
  { value: "Released", label: "Released" },
  { value: "NotInScope", label: "Not In Scope" },
  { value: "Completed", label: "Ready for Release" },
];

const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];

const AddSubTaskInline: React.FC<AddSubTaskInlineProps> = ({

  taskId,
  projectId,
  teamMembers,
  parentSubTaskId = null,
  parentTaskId = null,
  parentTaskDueDate,
  onSubTaskAdded,
  onCancel
}) => {
  const { role, department } = useAuth();

  const [formData, setFormData] = useState<SubTaskDTO>({
    taskId,
    parentSubTaskId,
    description: '',
    assignedToId: '',
    dueDate: null,
    status: 'YetToStart',
    priority: 'Medium',
    percentCompleted: 0,
    workHours: 0,
    completionDate: null,
    remarks: '',
    issues: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ✅ Correct Cancel trigger
  const handleCancel = () => {
    onCancel();     // call parent cancel
  };


  useEffect(() => {
    // Reset form when task or parentSubTaskId changes
    setFormData({
      taskId,
      parentSubTaskId,
      description: '',
      assignedToId: '',
      dueDate: null,
      status: 'YetToStart',
      priority: 'Medium',
      percentCompleted: 0,
      workHours: 0,
      completionDate: null,
      remarks: '',
      issues: '',
    });
    setError(null);
  }, [taskId, parentSubTaskId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'workHours' || name === 'percentCompleted'
        ? parseInt(value) || 0
        : value,
    }));
  };

  const validateForm = (data: SubTaskDTO): string | null => {
    if (!data.description.trim()) return 'Description is required';
    if (!data.assignedToId) return 'Assignee is required';
    if (!data.status) return 'Status is required';
    if (!data.priority) return 'Priority is required';

    // Due date must be strictly before parent task's due date
    if (parentTaskDueDate && data.dueDate && new Date(data.dueDate) >= new Date(parentTaskDueDate)) {
      return "Subtask Due Date must be strictly before the parent task's due date.";
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm(formData);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // createSubTask expects the subtask DTO shape; formData contains taskId & parentSubTaskId
      await createSubTask(formData);
      toast.success('Subtask created successfully');
      onSubTaskAdded();
      onCancel();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to create subtask';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Allow TeamLeader OR Admin to add subtasks
  if (role !== 'TeamLeader' && role !== 'Admin') {
    return <Alert color="danger">Only Team Leaders or Admins can add subtasks</Alert>;
  }

  // Admin sees all members; TeamLeader sees only same-department members
  const filteredTeamMembers = role === 'Admin'
    ? teamMembers
    : (!department ? teamMembers : teamMembers.filter(member => member.department === department));

  return (
    <Form onSubmit={handleSubmit}>
      <tr className='table-light'>
        <td style={{ width: '25%' }}>
          <Input
            name="description"
            placeholder='Subtask Description '
            value={formData.description}
            onChange={handleChange}
            disabled={loading}
            required
          />
        </td>



        <td style={{ width: '15%' }}>
          <Input
            type="select"
            name="assignedToId"
            value={formData.assignedToId}
            onChange={handleChange}
            disabled={loading}
            required
          >
            <option value="">Select Team Member</option>
            {filteredTeamMembers.map(member => (
              <option key={member.userID} value={member.userID}>
                {member.name}
              </option>
            ))}
          </Input>
        </td>

        <td style={{ width: '16%' }}>
          <Input
            type="select"
            name="status"
            value={formData.status}
            onChange={handleChange}
            disabled={loading}
          >
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </Input>
        </td>



        <td style={{ width: '14%' }}>
          <Input
            type="date"
            name="dueDate"
            value={formData.dueDate ?? ""}
            onChange={handleChange}
            disabled={loading}
            max={parentTaskDueDate ? (() => {
              const date = new Date(parentTaskDueDate);
              return date.toISOString().split('T')[0];
            })() : ''}
          />
        </td>
        <td style={{ width: '12%' }}>
          <Input
            type="select"
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            disabled={loading}
          >
            {priorityOptions.map(priority => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </Input>
        </td>

        <td style={{ width: '5%' }}>
          <Input
            type="number"
            name="percentCompleted"
            value={formData.percentCompleted}
            onChange={handleChange}
            min={0}
            max={100}
            disabled={loading}
          />
        </td>

        <td></td>

        <td style={{ width: '7%' }}>
          <div className="d-flex gap-2">
            <Button color="success" size="sm" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Save'}
            </Button>
            <Button
              color="secondary"
              size="sm"
              onClick={onCancel}
              className="d-flex align-items-center justify-content-center"
              style={{ width: "36px", height: "36px" }}
              title="Cancel"
            >
              <CancelIcon />
            </Button>
          </div>
          {error && <Alert color="danger" className="mt-2">{error}</Alert>}
        </td>
      </tr>
    </Form>
  );
};

export default AddSubTaskInline;
